import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { getClientAvatarBootstrap } from "@/app/actions/client-avatar/bootstrap/action";
import { ClientAvatarBootstrap } from "@/app/types/client-avatar/ClientAvatarBootstrap";
import { UCookie } from "@/app/utils/UCookie";
import { AvatarQuerySubKey, QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey } from "@/app/vars/Storage";

type Options = {
  projectId?: number;
  enabled?: boolean;
  initialData?: ClientAvatarBootstrap;
};

// Collapses the has-insights + offers -> avatars chain into one round-trip and seeds every
// downstream cache, so the page's initial load is a single Server Action instead of three
// serialized ones. The individual hooks stay gated on this and read the seeded caches; the
// child Avatars reads the seeded offer avatars, and offer switches flow through it as usual.
export function useFetchClientAvatarBootstrap(options: Options) {
  const { projectId, enabled = true, initialData } = options;
  const queryClient = useQueryClient();

  const seedCaches = (bootstrap: ClientAvatarBootstrap) => {
    queryClient.setQueryData([QueryKey.HAS_PROJECT_INSIGHTS, projectId], bootstrap.hasInsights);
    queryClient.setQueryData([QueryKey.PROJECT_OFFER, projectId], bootstrap.offers);
    if (bootstrap.selectedOfferId) {
      queryClient.setQueryData(
        [QueryKey.CLIENT_AVATAR, bootstrap.selectedOfferId],
        bootstrap.avatars,
      );

      if (bootstrap.freshness) {
        queryClient.setQueryData(
          [QueryKey.CLIENT_AVATAR, AvatarQuerySubKey.FRESHNESS, bootstrap.selectedOfferId],
          bootstrap.freshness,
        );
      }
    }
  };

  const seededProjectId = useRef<number | undefined>(undefined);
  if (initialData && projectId && seededProjectId.current !== projectId) {
    seedCaches(initialData);
    seededProjectId.current = projectId;
  }

  return useQuery({
    queryKey: [QueryKey.CLIENT_AVATAR_BOOTSTRAP, projectId],
    queryFn: async () => {
      const hintOfferId = UCookie.readNumber(CookieKey.SELECTED_OFFER);

      const bootstrap = await getClientAvatarBootstrap(projectId!, hintOfferId);
      seedCaches(bootstrap);

      return bootstrap;
    },
    enabled: enabled && !!projectId,
    staleTime: Infinity,
    initialData: seededProjectId.current === projectId ? initialData : undefined,
  });
}
